import React, { useState, useEffect, useCallback } from 'react';
import { AlertTriangle, CheckCircle2, Clock, Loader2, CalendarOff, Undo2 } from 'lucide-react';

// =====================================================================
// FINANCEIRO — Mapa de Caixa: PENDÊNCIAS (os dias sem mapa, por loja).
//
// A conciliação diária de recebimento de vendas exige o mapa de TODOS os dias no
// portal. Esta aba mostra, por loja, o que falta — em DUAS FAIXAS (decisão da Julia,
// 04/11/2026):
//   · ATRASADO   = anteontem para trás. É o que o Financeiro cobra (destaque vermelho).
//   · AGUARDANDO = ontem. Informativa: lançar no dia seguinte é o fluxo normal, então
//                  não pode ter cara de alarme (senão a manhã inteira parece atraso).
//
// ⚠️ A tela NÃO calcula pendência: quem deriva é o backend (`GET /pendencias`, regra
//    `diasPendentes`). Aqui só se desenha e se pede a dispensa. `podeDispensar` também
//    vem do servidor — a trava real é o `authorizeRoles` do POST.
// ⚠️ "Dispensar" = dia SEM CAIXA (loja fechada, feriado), com motivo. Não é para
//    esconder atraso: o motivo fica gravado com quem dispensou, e dá para desfazer.
// =====================================================================

const API = '/api/financeiro/mapa-caixa';
const COR = '#0d7a74';
const COR_FORTE = '#115e59';
const COR_FUNDO = '#f0fdfa';
const COR_BORDA = '#99f6e4';

const auth = () => ({ Authorization: `Bearer ${localStorage.getItem('authToken')}` });
const SEMANA = ['dom', 'seg', 'ter', 'qua', 'qui', 'sex', 'sáb'];
// 'AAAA-MM-DD' → 'qua 08/10'. Em UTC puro: sem fuso o dia nunca escorrega.
const rotuloDia = (iso) => {
  const [a, m, d] = String(iso).split('-').map(Number);
  const dia = new Date(Date.UTC(a, m - 1, d)).getUTCDay();
  return `${SEMANA[dia]} ${String(d).padStart(2, '0')}/${String(m).padStart(2, '0')}`;
};
const dataBr = (iso) => String(iso || '').split('-').reverse().join('/');

const estiloInput = { padding: '9px 12px', border: '1px solid #cbd5e1', borderRadius: 8, fontSize: 14, color: '#0f172a', background: '#fff', fontFamily: 'inherit', boxSizing: 'border-box' };

// Um dia pendente, como "chip". O botão de dispensar só existe para quem pode.
const Chip = ({ dia, tom, podeDispensar, onDispensar }) => {
  const cores = tom === 'atrasado'
    ? { bg: '#fef2f2', cor: '#991b1b', borda: '#fecaca' }
    : { bg: '#f8fafc', cor: '#475569', borda: '#e2e8f0' };
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', gap: 6, padding: '4px 10px', borderRadius: 999, whiteSpace: 'nowrap',
      background: cores.bg, color: cores.cor, border: `1px solid ${cores.borda}`,
      fontSize: 13, fontWeight: 700, fontVariantNumeric: 'tabular-nums',
    }}>
      {rotuloDia(dia)}
      {podeDispensar && (
        <button type="button" onClick={onDispensar} title="Dispensar: a loja não teve caixa nesse dia"
          style={{ display: 'inline-flex', background: 'none', border: 'none', padding: 0, cursor: 'pointer', color: cores.cor, opacity: 0.75 }}>
          <CalendarOff size={14} />
        </button>
      )}
    </span>
  );
};

const MapaCaixaPendencias = () => {
  const [dados, setDados] = useState(null);
  const [carregando, setCarregando] = useState(true);
  const [erro, setErro] = useState('');

  // Dispensa em andamento: { loja, data } da chip clicada.
  const [dispensando, setDispensando] = useState(null);
  const [motivo, setMotivo] = useState('');
  const [salvando, setSalvando] = useState(false);
  const [erroAcao, setErroAcao] = useState('');

  const carregar = useCallback(async () => {
    setCarregando(true);
    setErro('');
    try {
      const r = await fetch(`${API}/pendencias`, { headers: auth() });
      const corpo = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(corpo.message || 'Não foi possível carregar as pendências.');
      setDados(corpo);
    } catch (ex) {
      setErro(ex.message);
      setDados(null);
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => { carregar(); }, [carregar]);

  const abrirDispensa = (loja, data) => { setDispensando({ loja, data }); setMotivo(''); setErroAcao(''); };

  const enviar = async (url, corpo) => {
    setSalvando(true);
    setErroAcao('');
    try {
      const r = await fetch(`${API}${url}`, {
        method: 'POST',
        headers: { ...auth(), 'Content-Type': 'application/json' },
        body: JSON.stringify(corpo),
      });
      const resp = await r.json().catch(() => ({}));
      if (!r.ok) throw new Error(resp.message || 'Não foi possível concluir a ação.');
      setDispensando(null);
      await carregar();
    } catch (ex) {
      setErroAcao(ex.message);
    } finally {
      setSalvando(false);
    }
  };

  if (carregando && !dados) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, color: '#64748b', padding: 24 }}>
        <Loader2 size={18} className="spin" /> Carregando as pendências…
      </div>
    );
  }

  if (erro) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, padding: 14, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14 }}>
        <AlertTriangle size={18} /> <span>{erro}</span>
      </div>
    );
  }

  // Quem mais deve primeiro; em dia por último (e, entre iguais, ordem alfabética do servidor).
  const lojas = [...(dados?.lojas || [])].sort((a, b) => b.atrasados.length - a.atrasados.length);

  return (
    <div style={{ maxWidth: 980, width: '100%', margin: '0 auto', boxSizing: 'border-box', minWidth: 0 }}>
      <div style={{ marginBottom: 20 }}>
        <h3 style={{ margin: 0, fontSize: 22, fontWeight: 800, color: '#0f172a' }}>Dias sem mapa</h3>
        <p style={{ margin: '6px 0 0', color: '#64748b', fontSize: 14, lineHeight: 1.5 }}>
          A conciliação diária das vendas precisa do mapa de caixa de todos os dias. Contam os dias
          desde {dataBr(dados.corte)} até ontem.
          {dados.podeDispensar && ' Se a loja não teve caixa num dia (fechada, feriado), dispense-o informando o motivo.'}
        </p>
      </div>

      {/* Resumo das duas faixas */}
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 20 }}>
        <div style={{ flex: '1 1 140px', minWidth: 0, padding: 14, borderRadius: 12, background: dados.totais.atrasados ? '#fef2f2' : COR_FUNDO, border: `1px solid ${dados.totais.atrasados ? '#fecaca' : COR_BORDA}` }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: dados.totais.atrasados ? '#991b1b' : COR_FORTE, textTransform: 'uppercase', letterSpacing: 0.4 }}>Atrasados</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: dados.totais.atrasados ? '#b91c1c' : COR_FORTE, fontVariantNumeric: 'tabular-nums' }}>{dados.totais.atrasados}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>de anteontem para trás</div>
        </div>
        <div style={{ flex: '1 1 140px', minWidth: 0, padding: 14, borderRadius: 12, background: '#f8fafc', border: '1px solid #e2e8f0' }}>
          <div style={{ fontSize: 12, fontWeight: 800, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.4 }}>Aguardando</div>
          <div style={{ fontSize: 30, fontWeight: 800, color: '#475569', fontVariantNumeric: 'tabular-nums' }}>{dados.totais.aguardando}</div>
          <div style={{ fontSize: 12, color: '#64748b' }}>ontem — lançar no dia seguinte é normal</div>
        </div>
      </div>

      {/* Painel de dispensa (aparece ao clicar no ícone de uma chip) */}
      {dispensando && (
        <div style={{ padding: 14, marginBottom: 16, background: '#fffbeb', border: '1px solid #fcd34d', borderRadius: 10, fontSize: 14, color: '#92400e', boxSizing: 'border-box', overflowWrap: 'anywhere' }}>
          <strong>Dispensar {dispensando.loja} em {rotuloDia(dispensando.data)}</strong>
          <div style={{ margin: '4px 0 10px', fontSize: 13 }}>
            Use só quando a loja não teve caixa nesse dia. O motivo e o seu nome ficam registrados.
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 10, alignItems: 'center' }}>
            <input value={motivo} maxLength={200} onChange={(e) => setMotivo(e.target.value)} placeholder="Motivo (ex.: loja fechada, feriado)"
              style={{ ...estiloInput, flex: '1 1 200px', minWidth: 0, width: '100%' }} />
            <button type="button" disabled={salvando || motivo.trim().length < 3}
              onClick={() => enviar('/dispensa', { loja: dispensando.loja, data: dispensando.data, motivo })}
              style={{ padding: '9px 16px', background: salvando || motivo.trim().length < 3 ? '#cbd5e1' : COR, color: '#fff', border: 'none', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: salvando || motivo.trim().length < 3 ? 'default' : 'pointer' }}>
              {salvando ? 'Salvando…' : 'Dispensar o dia'}
            </button>
            <button type="button" disabled={salvando} onClick={() => setDispensando(null)}
              style={{ padding: '9px 16px', background: '#fff', color: '#334155', border: '1px solid #cbd5e1', borderRadius: 8, fontWeight: 700, fontSize: 14, cursor: 'pointer' }}>
              Cancelar
            </button>
          </div>
          {erroAcao && <div style={{ marginTop: 10, color: '#991b1b', fontWeight: 600 }}>{erroAcao}</div>}
        </div>
      )}
      {!dispensando && erroAcao && (
        <div style={{ padding: 12, marginBottom: 16, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 14 }}>{erroAcao}</div>
      )}

      {/* Uma linha por loja */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        {lojas.map((l) => {
          const emDia = !l.atrasados.length && !l.aguardando.length;
          return (
            <div key={l.loja} style={{ border: '1px solid #e2e8f0', borderRadius: 12, padding: '12px 14px', background: '#fff', boxSizing: 'border-box', minWidth: 0 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                <strong style={{ fontSize: 16, color: '#0f172a', minWidth: 48 }}>{l.loja}</strong>
                {emDia ? (
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, fontWeight: 700, color: '#047857' }}>
                    <CheckCircle2 size={16} /> Em dia
                  </span>
                ) : (
                  <>
                    {l.atrasados.length > 0 && (
                      <span style={{ fontSize: 13, fontWeight: 800, color: '#b91c1c' }}>
                        {l.atrasados.length} {l.atrasados.length === 1 ? 'dia atrasado' : 'dias atrasados'}
                      </span>
                    )}
                    {l.aguardando.length > 0 && (
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: 13, fontWeight: 600, color: '#64748b' }}>
                        <Clock size={14} /> aguardando o de ontem
                      </span>
                    )}
                  </>
                )}
              </div>

              {(l.atrasados.length > 0 || l.aguardando.length > 0) && (
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                  {l.atrasados.map((d) => (
                    <Chip key={d} dia={d} tom="atrasado" podeDispensar={dados.podeDispensar} onDispensar={() => abrirDispensa(l.loja, d)} />
                  ))}
                  {l.aguardando.map((d) => (
                    <Chip key={d} dia={d} tom="aguardando" podeDispensar={dados.podeDispensar} onDispensar={() => abrirDispensa(l.loja, d)} />
                  ))}
                </div>
              )}

              {/* Dias já dispensados: ficam visíveis (transparência) e podem ser desfeitos. */}
              {l.dispensados.length > 0 && (
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: '1px dashed #e2e8f0', display: 'flex', flexDirection: 'column', gap: 4 }}>
                  {l.dispensados.map((x) => (
                    <div key={x.data} style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', fontSize: 12, color: '#64748b' }}>
                      <CalendarOff size={12} />
                      <span style={{ minWidth: 0, overflowWrap: 'anywhere' }}><strong>{rotuloDia(x.data)}</strong> dispensado — {x.motivo} ({x.por})</span>
                      {dados.podeDispensar && (
                        <button type="button" disabled={salvando} onClick={() => enviar('/dispensa/cancelar', { loja: l.loja, data: x.data })}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: 4, background: 'none', border: 'none', color: COR_FORTE, fontWeight: 700, fontSize: 12, cursor: 'pointer', padding: 0 }}>
                          <Undo2 size={12} /> desfazer
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default MapaCaixaPendencias;
